import { createAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';

import dashboardService from '../services/dashboardService';
import { duplicateScribeFile } from '../v2/slices/workspace';
import { archiveDocumentMetadata, deleteDocumentMetadata } from './actions/documentMetadataActions';
import { signOut } from './authSlice';
import { updateFilePermission, updateScribeFile } from './scribeFileSlice';

export const getDashboardRecentFiles = createAsyncThunk('newDashboard/getDashboardRecentFiles', async () => {
  const files = await dashboardService.recent();

  return files;
});

export const getDashboardRecentSummary = createAsyncThunk('newDashboard/getDashboardRecentSummary', async () => {
  const files = await dashboardService.recentSummary();

  return files;
});

export const getDashboardCreatedFiles = createAsyncThunk('newDashboard/getDashboardCreatedFiles', async ({ sortParam, skip }) => {
  const files = await dashboardService.created({ sortParam, skip });

  return files;
});
export const getDashboardSharedFiles = createAsyncThunk('newDashboard/getDashboardSharedFiles', async ({ sortParam, skip }) => {
  const files = await dashboardService.shared({ sortParam, skip });

  return files;
});

// endpoint for user's most popular documents and stats in case we need it
//  https://www.figma.com/file/K1317qnXWy90qFTdoHWrzS/Dashboard?node-id=683%3A20744
export const getDashboardMostPopularDocuments = createAsyncThunk('newDashboard/mostPopularDocuments', async () => {
  const stats = await dashboardService.mostPopularDocuments();

  return stats;
});

export const listDashboardStats = createAsyncThunk('newDashboard/stats', async () => {
  const stats = await dashboardService.stats();

  return stats;
});

export const setActiveDashboardTab = createAction('newDashboard/setActiveTab', (tab) => {
  return {
    payload: tab
  };
});

const DOCUMENT_KEYS = ['created_documents', 'recent_documents', 'shared_with_user'];

const initialState = {
  files: null,
  stats: null,
  loadingRecent: false,
  loadingRecentError: null,
  loadingCreated: false,
  loadingCreatedError: null,
  loadingShared: false,
  loadingSharedError: null,
  loadingStats: false,
  loadingStatsError: null,
  activeTab: DOCUMENT_KEYS[1]
};

const newDashboardSlice = createSlice({
  name: 'newDashboard',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getDashboardRecentFiles.pending, (state) => {
        state.loadingRecent = true;
        state.loadingRecentError = null;
      })
      .addCase(getDashboardRecentFiles.fulfilled, (state, action) => {
        state.files = { ...state.files, ...action.payload };
        state.loadingRecent = false;
        state.loadingRecentError = null;
      })
      .addCase(getDashboardRecentFiles.rejected, (state, action) => {
        state.loadingRecent = false;
        state.loadingRecentError = action.payload;
      })
      .addCase(getDashboardRecentSummary.pending, (state) => {
        state.loadingRecent = true;
        state.loadingRecentError = null;
      })
      .addCase(getDashboardRecentSummary.fulfilled, (state, action) => {
        state.files = { ...state.files, ...action.payload };
        state.loadingRecent = false;
        state.loadingRecentError = null;
      })
      .addCase(getDashboardRecentSummary.rejected, (state, action) => {
        state.loadingRecent = false;
        state.loadingRecentError = action.payload;
      })
      .addCase(getDashboardCreatedFiles.pending, (state) => {
        state.loadingCreated = true;
        state.loadingCreatedError = null;
      })
      .addCase(getDashboardCreatedFiles.fulfilled, (state, action) => {
        const { skip } = action.meta.arg;
        const { created_documents } = action.payload;
        // if there is a skip argument append created_documents with the payload

        if (skip) {
          state.files = { ...state.files, created_documents: [...state.files.created_documents, ...created_documents] };
        } else {
          state.files = { ...state.files, ...action.payload };
        }
        state.loadingCreated = false;
        state.loadingCreatedError = null;
      })
      .addCase(getDashboardCreatedFiles.rejected, (state, action) => {
        state.loadingCreated = false;
        state.loadingCreatedError = action.payload;
      })
      .addCase(getDashboardSharedFiles.pending, (state) => {
        state.loadingShared = true;
        state.loadingSharedError = null;
      })
      .addCase(getDashboardSharedFiles.fulfilled, (state, action) => {
        const { skip } = action.meta.arg;
        const { shared_with_user } = action.payload;
        // if there is a skip argument append shared_with_user documents with the payload

        if (skip) {
          state.files = { ...state.files, shared_with_user: [...state.files.shared_with_user, ...shared_with_user] };
        } else {
          state.files = { ...state.files, ...action.payload };
        }
        state.loadingShared = false;
        state.loadingSharedError = null;
      })
      .addCase(getDashboardSharedFiles.rejected, (state, action) => {
        state.loadingShared = false;
        state.loadingSharedError = action.payload;
      })
      .addCase(archiveDocumentMetadata.fulfilled, (state, action) => {
        const id = action.meta.arg;

        if (state.files) {
          DOCUMENT_KEYS.forEach((k) => {
            state.files[k] = state.files[k]?.filter((doc) => doc.id !== id);
          });
          state.stats.number_of_created -= 1;
        }
      })
      .addCase(deleteDocumentMetadata.fulfilled, (state, action) => {
        const id = action.meta.arg;

        if (state.files) {
          DOCUMENT_KEYS.forEach((k) => {
            state.files[k] = state.files[k]?.filter((doc) => doc.id !== id);
          });
          state.stats.number_of_created -= 1;
        }
      })
      .addCase(duplicateScribeFile.fulfilled, (state, action) => {
        const { retry } = action.meta.arg;

        if (state.files) {
          const createdDocuments = state.files.created_documents || [];

          state.files.created_documents = [
            ...(retry
              ? []
              : [
                  {
                    ...action.payload,
                    type: 'scribe'
                  }
                ]),
            ...createdDocuments
          ];
          state.stats.number_of_created += 1;
          state.activeTab = DOCUMENT_KEYS[0];
        }
      })
      .addCase(updateScribeFile.fulfilled, (state, action) => {
        if (state.files) {
          DOCUMENT_KEYS.forEach((k) => {
            state.files[k] = state.files[k]?.map((doc) => {
              if (doc.id === action.payload.id) {
                return action.payload;
              }
              return doc;
            });
          });
        }
      })
      .addCase(updateFilePermission.pending, (state, action) => {
        const { documentId, bodyParams } = action.meta.arg;
        const permissionLevel = bodyParams?.global_permission;

        if (state.files) {
          DOCUMENT_KEYS.forEach((k) => {
            state.files[k] = state.files[k]?.map((doc) => {
              if (doc.id === documentId) {
                return {
                  ...doc,
                  global_permission: permissionLevel || doc.global_permission
                };
              }
              return doc;
            });
          });
        }
      })
      .addCase(updateFilePermission.fulfilled, (state, action) => {
        const updatedDocument = action.payload;

        if (state.files) {
          DOCUMENT_KEYS.forEach((k) => {
            state.files[k] = state.files[k]?.map((doc) => {
              if (doc.id === updatedDocument.id) {
                return updatedDocument;
              }
              return doc;
            });
          });
        }
      })
      .addCase(updateFilePermission.rejected, (state, action) => {
        const { document } = action.meta.arg;

        if (state.files) {
          DOCUMENT_KEYS.forEach((k) => {
            state.files[k] = state.files[k]?.map((doc) => {
              if (doc.id === document.id) {
                return {
                  ...doc,
                  global_permission: document.global_permission
                };
              }
              return doc;
            });
          });
        }
      })
      .addCase(listDashboardStats.pending, (state) => {
        state.loadingStats = true;
        state.loadingStatsError = false;
      })
      .addCase(listDashboardStats.fulfilled, (state, action) => {
        state.stats = action.payload;
        state.loadingStats = false;
        state.loadingStatsError = false;
      })
      .addCase(listDashboardStats.rejected, (state) => {
        state.loadingStats = false;
        state.loadingStatsError = true;
      })
      .addCase(setActiveDashboardTab, (state, action) => {
        state.activeTab = action.payload;
      })
      .addCase(signOut, () => initialState);
  }
});

export const selectNewDashboard = createSelector(
  [
    (state) => state.newDashboard.files,
    (state) => state.newDashboard.stats,
    (state) => state.newDashboard.loadingRecent,
    (state) => state.newDashboard.loadingCreated,
    (state) => state.newDashboard.files?.total_created,
    (state) => state.newDashboard.loadingShared,
    (state) => state.newDashboard.files?.total_shared,
    (state) => state.newDashboard.loadingStats,
    (state) => state.newDashboard.activeTab
  ],
  (files, stats, loadingRecent, loadingCreated, totalCreated, loadingShared, totalShared, loadingStats, activeTab) => ({
    files,
    stats,
    loadingRecent,
    loadingCreated,
    totalCreated: totalCreated ?? 0,
    loadingShared,
    totalShared: totalShared ?? 0,
    loadingStats,
    activeTab
  })
);

export const selectDashboardStats = createSelector(
  [(state) => state.newDashboard.stats, (state) => state.newDashboard.loadingStatsError, (state) => state.newDashboard.loadingStats],
  (stats, isError, isLoading) => ({ stats, isError, isLoading })
);

export default newDashboardSlice.reducer;
